import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const PhaseTimeline = ({ phases, errors, onChange, workers = [] }) => {
  const [expandedPhases, setExpandedPhases] = useState({});

  const standardPhases = [
    { id: 1, name: 'Hacking', icon: 'Hammer', description: 'Demolition and removal work' },
    { id: 2, name: '3 Musketeers', icon: 'Users', description: 'Plumbing, electrical, and aircon rough-in' },
    { id: 3, name: 'False Ceiling', icon: 'Box', description: 'Ceiling installation and framework' },
    { id: 4, name: 'Tiling', icon: 'Grid3x3', description: 'Floor and wall tile installation' },
    { id: 5, name: 'Carpentry Measurement', icon: 'Ruler', description: 'Measurements for custom carpentry' },
    { id: 6, name: 'Clearing', icon: 'Trash2', description: 'Site cleanup and debris removal' },
    { id: 7, name: 'Painting', icon: 'Paintbrush', description: 'Wall and ceiling painting' },
    { id: 8, name: 'Carpentry Installation', icon: 'Wrench', description: 'Cabinet and fixture installation' },
    { id: 9, name: '3 Musketeers Installation', icon: 'Plug', description: 'Final electrical, plumbing, aircon setup' },
    { id: 10, name: 'Paint Touch-Up', icon: 'Brush', description: 'Final paint corrections' },
    { id: 11, name: 'Cleaning', icon: 'Sparkles', description: 'Final deep cleaning' },
    { id: 12, name: 'Handover', icon: 'CheckCircle', description: 'Project completion and handover' }
  ];

  const workerOptions = workers?.map(w => ({
    value: w?.id,
    label: w?.full_name || w?.email || w?.id
  }));

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev?.[phaseId]
    }));
  };

  const handlePhaseToggle = (phaseId, enabled) => {
    onChange(phaseId, 'enabled', enabled);

    if (enabled) {
      const currentIndex = standardPhases?.findIndex(p => p?.id === phaseId);

      if (currentIndex > 0) {
        for (let i = currentIndex - 1; i >= 0; i--) {
          const prevPhaseId = standardPhases?.[i]?.id;
          const prevPhaseData = phases?.[prevPhaseId];

          if (prevPhaseData?.enabled && prevPhaseData?.endDate) {
            onChange(phaseId, 'startDate', prevPhaseData?.endDate);
            break;
          }
        }
      }

      setExpandedPhases(prev => ({
        ...prev,
        [phaseId]: true
      }));
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">Phase Timeline</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enable phases and assign a worker to each one
        </p>
      </div>
      <div className="space-y-3">
        {standardPhases?.map((phase, index) => {
          const phaseData =
            phases?.[phase?.id] || {
              enabled: false,
              startDate: '',
              endDate: '',
              assigned_worker_id: ''
            };

          const isExpanded = expandedPhases?.[phase?.id];

          return (
            <div
              key={phase?.id}
              className="bg-muted/30 rounded-lg border border-border overflow-hidden"
            >
              <div className="p-4">
                {/* HEADER */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center justify-center w-8 h-8 bg-background rounded-lg border border-border">
                      <span className="text-sm font-semibold text-foreground">
                        {index + 1}
                      </span>
                    </div>

                    <Icon
                      name={phase?.icon}
                      size={20}
                      color="var(--color-primary)"
                      strokeWidth={2}
                    />

                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-foreground">
                        {phase?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {phase?.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Checkbox
                      label="Enable"
                      checked={phaseData?.enabled}
                      onChange={(e) =>
                        handlePhaseToggle(phase?.id, e?.target?.checked)
                      }
                      size="sm"
                    />

                    {phaseData?.enabled && (
                      <button
                        type="button"
                        onClick={() => togglePhase(phase?.id)}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-smooth"
                      >
                        <Icon
                          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                          size={20}
                          strokeWidth={2}
                        />
                      </button>
                    )}
                  </div>
                </div>

                {/* EXPANDED SECTION */}
                {phaseData?.enabled && isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border space-y-5">
                    
                    {/* Worker Assignment */}
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-xs font-semibold text-primary mb-3 uppercase tracking-wide">
                        Worker Assignment
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Required Trade
                          </label>
                          <div className="px-3 py-2 bg-muted rounded-md border border-border text-sm text-foreground">
                            {phase?.name}
                          </div>
                        </div>

                        <Select
                          label="Assign Worker"
                          options={workerOptions}
                          value={phaseData?.assigned_worker_id || ''}
                          onChange={(value) =>
                            onChange(phase?.id, 'assigned_worker_id', value)
                          }
                          placeholder="Select a worker"
                          error={errors?.[`phase_${phase?.id}_worker`]}
                        />
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Suggested Start Date"
                        type="date"
                        value={phaseData?.startDate || ''}
                        onChange={(e) =>
                          onChange(phase?.id, 'startDate', e?.target?.value)
                        }
                        error={errors?.[`phase_${phase?.id}_start`]}
                      />

                      <Input
                        label="Suggested End Date"
                        type="date"
                        value={phaseData?.endDate || ''}
                        min={phaseData?.startDate || undefined}
                        onChange={(e) =>
                          onChange(phase?.id, 'endDate', e?.target?.value)
                        }
                        error={errors?.[`phase_${phase?.id}_end`]}
                      />
                    </div>

                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PhaseTimeline;